import { useEffect, useRef } from 'react';

/**
 * Focus trap para modais e dialogs (WCAG 2.4.3 + 2.1.2).
 *
 * - Salva o elemento focado antes de abrir (geralmente o botão que disparou o modal).
 * - Move foco para o primeiro elemento focável dentro do container.
 * - Circula Tab / Shift+Tab apenas entre elementos focáveis do container.
 * - Esc chama onEscape (tipicamente o close handler).
 * - Ao desmontar / desativar, restaura foco no elemento salvo.
 *
 * Uso:
 *   const ref = useFocusTrap<HTMLDivElement>({ active: true, onEscape: onClose });
 *   return <div ref={ref} role="dialog" ...>...</div>;
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

interface UseFocusTrapOptions {
  active?: boolean;
  onEscape?: () => void;
}

export function useFocusTrap<T extends HTMLElement>(
  options: UseFocusTrapOptions = {}
) {
  const { active = true, onEscape } = options;
  const containerRef = useRef<T | null>(null);

  // onEscape quase sempre chega como arrow inline, ou seja, com identidade
  // nova a cada render do pai. Com ele nas deps do efeito, digitar uma letra
  // num campo do modal recriava o trap e devolvia o foco para o primeiro
  // focável (o X de fechar): dava para digitar um caractere por clique.
  // A ref mantém o handler atual sem fazer o efeito rodar de novo.
  const onEscapeRef = useRef(onEscape);
  useEffect(() => {
    onEscapeRef.current = onEscape;
  });

  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    const getFocusable = (): HTMLElement[] =>
      Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
        .filter((el) => !el.hasAttribute('aria-hidden') && el.offsetParent !== null);

    // Move foco para o primeiro elemento (ou para o próprio container como fallback).
    const focusables = getFocusable();
    if (focusables.length > 0) {
      focusables[0].focus();
    } else {
      // Container precisa tabIndex=-1 para receber foco; adiciona se necessário.
      if (!container.hasAttribute('tabindex')) {
        container.setAttribute('tabindex', '-1');
      }
      container.focus();
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onEscapeRef.current) {
        e.stopPropagation();
        onEscapeRef.current();
        return;
      }
      if (e.key !== 'Tab') return;

      const currentFocusables = getFocusable();
      if (currentFocusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = currentFocusables[0];
      const last = currentFocusables[currentFocusables.length - 1];
      const activeEl = document.activeElement as HTMLElement | null;

      if (e.shiftKey && activeEl === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && activeEl === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Restaura foco se o elemento ainda existir e for focável.
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
    // Só `active`: a montagem do trap (e o foco inicial) acontece na transição
    // de fechado para aberto, não a cada tecla digitada dentro do modal.
  }, [active]);

  return containerRef;
}
